import * as React from 'react';
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import { Box, useTheme, Typography } from "@mui/material";
import { FileUploadOutlined } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { tokens } from "../../../theme";
import ClearIcon from '@mui/icons-material/Clear';
import moment from 'moment';
import Axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import { getAuthHeader } from '../../../utils/authToken';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import QuestionRender from '../../../components/QuestionRender';
import { CoordinateGrid, formatElementSummary, parseCoordinateAnswerForDisplay } from '../../../components/coordinateGrid';
import { TableReadOnly, defaultTableConfig } from '../../../components/tableAnswer';
import { MappingReadOnly, defaultMappingConfig } from '../../../components/mappingAnswer';
import { PolynomialGraphReadOnly, defaultPolynomialGraphConfig } from '../../../components/polynomialGraph';
import { RationalGraphReadOnly, defaultRationalGraphConfig } from '../../../components/rationalGraph';
import { TrigGraphReadOnly, defaultTrigGraphConfig } from '../../../components/trigGraph';
import { MultilineReadOnly, defaultMultilineConfig } from '../../../components/multiline';
import { NumberLineReadOnly, defaultNumberLineConfig } from '../../../components/numberLine';
import { MATHLIVE_ANSWER_TYPES, parseMathliveAnswer } from '../../../components/mathlive';

var utc = require('dayjs/plugin/utc')
var timezone = require('dayjs/plugin/timezone');

dayjs.extend(utc)
dayjs.extend(timezone)

// Convert flat pages array [1,2,3,5,6] to ranges [{from:1,to:3},{from:5,to:6}]
function pagesToRanges(pages) {
    if (!pages || pages.length === 0) return [{ from: 0, to: 0 }];
    const sorted = [...pages].sort((a, b) => a - b);
    const ranges = [];
    let start = sorted[0];
    let end = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === end + 1) {
            end = sorted[i];
        } else {
            ranges.push({ from: start, to: end });
            start = sorted[i];
            end = sorted[i];
        }
    }
    ranges.push({ from: start, to: end });
    return ranges;
}

// Helper to format coordinate answer for display
const formatCoordinateAnswerDisplay = (answer, config, _colors) => {
    const parsed = parseCoordinateAnswerForDisplay(answer, config, 150);
    if (!parsed) return null;

    const { elements, gridConfig, gridSize } = parsed;

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mt: 1 }}>
            <CoordinateGrid
                config={gridConfig}
                elements={elements}
                pendingPoints={[]}
                mode="point"
                readOnly={true}
                width={gridSize}
                height={gridSize}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {elements.map((el, i) => (
                    <Chip
                        key={el.id || i}
                        label={formatElementSummary(el)}
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                    />
                ))}
            </Box>
        </Box>
    );
};


export default function UpdateModal({
    courseId,
    homework,
    students,
    classMaterials,
    userRole,
    studentsStartEndDateMap,
    authorDisabledForAdminActions,
    setBackdropOpen,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // ── HW Questions detection ──
    const isHwQuestions = homework.homework_type === 'hw_questions';

    // ── Submission status check ──
    const hasSubmissions = (homework.studentsWhoSubmitted?.length > 0) ||
                           (homework.studentsWhoSubmittedLate?.length > 0);
    const hasDrafts = homework.hasDraftSubmissions === true;

    // 'blocked' = students submitted, can't edit pages/exceptions
    // 'warning' = students have drafts, show warning but allow edit
    // 'free' = no interaction, free to edit
    const hwQuestionsEditMode = hasSubmissions ? 'blocked' : (hasDrafts ? 'warning' : 'free');

    // ── Find matching class material ──
    const matchedClassMaterial = isHwQuestions
        ? (classMaterials || []).find(cm => cm.id === homework.class_material_id) || null
        : null;

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isHwQuestions ? 1000 : 800,
        maxHeight: '90%',
        overflowY: 'auto',
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    const lecture_start_date = homework.event.start.split('T')[0];
    let current_students = [];

    for (let i = 0; i < students.length; i++) {
        const end_date = dayjs(studentsStartEndDateMap[students[i].id].end_date);
        const start_date = dayjs(studentsStartEndDateMap[students[i].id].start_date);

        if (dayjs(lecture_start_date).isAfter(end_date) || dayjs(lecture_start_date).isBefore(start_date)) {
            // student was not in the class when the homework was posted
            continue;
        }
        current_students.push(students[i]);
    }

    let exclude_students = [];
    let unexclude_students = [];
    if (homework.exclude_student !== null) {
        const exclude_student = homework.exclude_student.split('|');
        exclude_students = exclude_student;
        unexclude_students = homework.unexclude_student.split('|');
    }

    let exclude_candidates = [];
    let unexclude_candidates = [];
    let current_students_id = current_students.map(student => student.id);

    for (let i = 0; i < students.length; i++) {
        if (!current_students_id.includes(students[i].id))
            continue;

        // a student is not in both exclude_students and unexclude_students,
        // we should consider this student as a unexclude candidate

        if (!exclude_students.includes(students[i].id.toString()) && !unexclude_students.includes(students[i].id.toString())) {
            unexclude_candidates.push(students[i]);
            continue;
        }

        if (!exclude_students.includes(students[i].id.toString())) {
            exclude_candidates.push(students[i]);
        } else if (exclude_students.includes(students[i].id.toString())) {
            unexclude_candidates.push(students[i]);
        }
    }

    // for homework
    const [dueDate, setDueDate] = React.useState(homework.due_date ? moment(homework.due_date) : null);
    const [files, setFiles] = React.useState(homework.hw_files ? homework.hw_files : []);
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const [newExcludeStudents, setnewExcludeStudents] = React.useState([]);
    const [newUnexcludeStudents, setNewUnexcludeStudents] = React.useState([]);

    // ── HW Questions state (only used when isHwQuestions) ──
    const [pageRanges, setPageRanges] = React.useState(() => {
        if (!isHwQuestions) return [{ from: 0, to: 0 }];
        if (homework.pages && homework.pages.length > 0) {
            return pagesToRanges(homework.pages);
        }
        // Fallback to page_from/page_to
        if (homework.page_from && homework.page_to) {
            return [{ from: homework.page_from, to: homework.page_to }];
        }
        return [{ from: 0, to: 0 }];
    });
    const [hwQuestions, setHwQuestions] = React.useState([]);
    const [excludedQuestions, setExcludedQuestions] = React.useState(
        homework.exceptions || []
    );
    const [excludedQuestionLabels, setExcludedQuestionLabels] = React.useState([]);
    const [tinyLoading, setTinyLoading] = React.useState(false);
    const [showQuestions, setShowQuestions] = React.useState(true);
    const [autoRemovedPages, setAutoRemovedPages] = React.useState([]);

    // ── Fetch questions on modal open ──
    React.useEffect(() => {
        if (open && isHwQuestions && matchedClassMaterial) {
            const validRanges = pageRanges.filter(r => r.from > 0 && r.to >= r.from);
            if (validRanges.length > 0) {
                fetchHWQuestionsWithRanges(validRanges);
            }
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Page range helpers ──
    const addPageRange = () => {
        setPageRanges([...pageRanges, { from: 0, to: 0 }]);
        setExcludedQuestions([]);
        setExcludedQuestionLabels([]);
        setAutoRemovedPages([]);
    };

    const removePageRange = (index) => {
        if (pageRanges.length > 1) {
            const updated = pageRanges.filter((_, i) => i !== index);
            setPageRanges(updated);
            const validRanges = updated.filter(r => r.from > 0 && r.to >= r.from);
            if (validRanges.length > 0) {
                fetchHWQuestionsWithRanges(validRanges);
            } else {
                setHwQuestions([]);
            }
            setExcludedQuestions([]);
            setExcludedQuestionLabels([]);
            setAutoRemovedPages([]);
        }
    };

    const updatePageRange = (index, field, value) => {
        const updated = [...pageRanges];
        updated[index] = { ...updated[index], [field]: parseInt(value) || 0 };
        if (field === 'from') updated[index].to = 0;
        setPageRanges(updated);
        setExcludedQuestions([]);
        setExcludedQuestionLabels([]);
        setAutoRemovedPages([]);
        if (field === 'to' && value > 0) {
            const validRanges = updated.filter(r => r.from > 0 && r.to >= r.from);
            if (validRanges.length > 0) fetchHWQuestionsWithRanges(validRanges);
        }
    };

    const getSelectedPages = () => {
        const pages = new Set();
        pageRanges.forEach(range => {
            if (range.from > 0 && range.to >= range.from) {
                for (let p = range.from; p <= range.to; p++) pages.add(p);
            }
        });
        return Array.from(pages).sort((a, b) => a - b);
    };

    const hasValidRanges = () => pageRanges.some(r => r.from > 0 && r.to >= r.from);

    // ── Fetch HW Questions ──
    const fetchHWQuestionsWithRanges = async (ranges) => {
        if (!matchedClassMaterial || !matchedClassMaterial.textbook_id) return;

        const pages = [];
        ranges.forEach(range => {
            if (range.from > 0 && range.to >= range.from) {
                for (let p = range.from; p <= range.to; p++) {
                    if (!pages.includes(p)) pages.push(p);
                }
            }
        });
        if (pages.length === 0) return;

        setAutoRemovedPages([]);
        setTinyLoading(true);
        try {
            const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/hw-questions`, {
                params: {
                    textbook_id: matchedClassMaterial.textbook_id,
                    pages: pages.join(','),
                },
                headers: { Authorization: getAuthHeader() },
            });
            setHwQuestions(response.data);
        } catch (error) {
            console.log(error);
            setErrorAlertMessage?.(error.response?.data?.message || "Error fetching questions");
            setErrorAlertOpen?.(true);
        }
        setTinyLoading(false);
    };

    const handleFileRemove = (event, index) => {
        event.preventDefault();
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isHwQuestions && hwQuestionsEditMode !== 'blocked') {
            if (!hasValidRanges()) {
                setErrorAlertMessage("Please select at least one valid page range!");
                setErrorAlertOpen(true);
                return;
            }
            // matchedClassMaterial guard: if the class material is missing from the
            // list, questions can never be fetched — let the server validate instead
            // of blocking title/due-date edits here.
            if (matchedClassMaterial && hwQuestions.filter(q => !excludedQuestions.includes(q.id)).length === 0) {
                setErrorAlertMessage("The selected pages do not contain any questions. Please select pages with at least one question included.");
                setErrorAlertOpen(true);
                return;
            }
        }

        setBackdropOpen(true);

        // ── Exclude/Unexclude student logic ──
        let selected_exclude_students = [];
        if (newExcludeStudents !== null && newExcludeStudents.length > 0) {
            for (let i = 0; i < newExcludeStudents.length; i++) {
                selected_exclude_students += newExcludeStudents[i].id;
                if (i !== newExcludeStudents.length - 1) {
                    selected_exclude_students += "|";
                }
            }
        }

        let selected_unexclude_students = [];
        if (newUnexcludeStudents !== null && newUnexcludeStudents.length > 0) {
            for (let i = 0; i < newUnexcludeStudents.length; i++) {
                selected_unexclude_students += newUnexcludeStudents[i].id;
                if (i !== newUnexcludeStudents.length - 1) {
                    selected_unexclude_students += "|";
                }
            }
        }

        if (isHwQuestions) {
            // ── HW Questions: JSON submission ──
            const data = new FormData(event.currentTarget);
            const payload = {
                title: data.get('title'),
                posted_by: data.get('author'),
                content: data.get('instructions'),
                due_date: dueDate,
                exclude_student: selected_exclude_students.toString(),
                unexclude_student: selected_unexclude_students.toString(),
            };

            // Only include pages/exceptions if editing is allowed
            if (hwQuestionsEditMode !== 'blocked') {
                payload.pages = getSelectedPages();
                payload.exceptions = excludedQuestions;
            }

            try {
                await Axios.put(
                    `${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/events/${homework.event_id}/homeworks/${homework.id}`,
                    payload,
                    {
                        headers: {
                            Authorization: getAuthHeader(),
                            'Content-Type': 'application/json',
                        },
                    }
                );
                setSuccessAlertMessage("Homework updated successfully!");
                setSuccessAlertOpen(true);
                setBackdropOpen(false);
                window.location.reload();
            } catch (error) {
                console.log(error);
                setBackdropOpen(false);
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Error updating homework");
                setErrorAlertOpen(true);
            }
        } else {
            // ── File Upload: existing FormData/multipart submission ──
            const data = new FormData(event.currentTarget);

            const formData = new FormData();
            for (let i = 0; i < files.length; i++) {
                if (files[i] instanceof File) {
                    if (files[i].name !== undefined
                        && files[i].name !== null
                        && files[i].name !== ""
                        && files[i].name.length > 150) {
                        setErrorAlertMessage("File name should be less than 150 characters!");
                        setErrorAlertOpen(true);
                        setBackdropOpen(false);
                        return;
                    }
                    formData.append('file', files[i], files[i].name);
                } else
                    formData.append('old_files', files[i].name);
            }

            formData.append('title', data.get('title'));
            formData.append('posted_by', data.get('author'));
            formData.append('content', data.get('instructions'));
            formData.append('due_date', dueDate);
            formData.append('exclude_student', selected_exclude_students);
            formData.append('unexclude_student', selected_unexclude_students);

            try {
                await Axios.put(
                    `${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/events/${homework.event_id}/homeworks/${homework.id}`,
                    formData,
                    {
                        headers: {
                            Authorization: getAuthHeader(),
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
                setSuccessAlertMessage("Homework updated successfully!");
                setSuccessAlertOpen(true);
                setBackdropOpen(false);
                window.location.reload();
            } catch (error) {
                console.log(error);
                setBackdropOpen(false);
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Error updating homework");
                setErrorAlertOpen(true);
            }
        }
    };

    const handleUpload = (event) => {
        setFiles([...files, ...Array.from(event.target.files)]);
    };

    return (
        <div>
            <Button onClick={handleOpen} sx={{ p: 1, width: "100%" }}>Update</Button>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Box
                            component="form"
                            sx={{
                                '& > :not(style)': { m: 1, width: '25ch' },
                                display: "flex",
                                flexDirection: "column",
                                mr: "1rem",
                            }}
                            noValidate
                            autoComplete="off"
                            onSubmit={handleSubmit}
                        >
                            <Box sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "1rem",
                                minWidth: "100%",
                            }}>
                                <TextField
                                    id="filled-basic"
                                    label="Title"
                                    variant="filled"
                                    name='title'
                                    sx={{
                                        flex: "1",
                                        maxWidth: "50%",
                                        '& .MuiFilledInput-root': {
                                            backgroundColor: colors.primary[600],
                                            ":focus": {
                                                backgroundColor: 'white',
                                            },
                                        },
                                    }}
                                    defaultValue={homework.title}
                                />
                                <TextField
                                    id="filled-basic"
                                    label="Author"
                                    variant="filled"
                                    name='author'
                                    sx={{
                                        flex: "1",
                                        maxWidth: "50%",
                                        '& .MuiFilledInput-root': {
                                            backgroundColor: colors.primary[600],
                                            ":focus": {
                                                backgroundColor: 'white',
                                            },
                                        },
                                    }}
                                    defaultValue={localStorage.getItem('userName')}
                                    disabled={authorDisabledForAdminActions}
                                />
                            </Box>

                        {userRole === 'admin' ?
                            (
                            <>
                                <Box sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "1rem",
                                minWidth: "100%",
                                }}>
                                    {/* a list of students*/}
                                    <Autocomplete
                                        multiple
                                        limitTags={4}
                                        id="student-list"
                                        options={exclude_candidates ? exclude_candidates : []}
                                        getOptionLabel={(exclude_candidates) => exclude_candidates.first_name + " " + exclude_candidates.last_name}
                                        getOptionKey={(student) => student.id}
                                        filterSelectedOptions
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="추가로 숙제에서 제외할 학생을 선택하세요."
                                            />
                                        )}
                                        sx={{ width: "100%" }}
                                        onChange={(event, value) => {
                                            setnewExcludeStudents(value);
                                        }}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                </Box>
                                <Box sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "1rem",
                                minWidth: "100%",
                                }}>
                                    {/* a list of students*/}
                                    <Autocomplete
                                        multiple
                                        limitTags={4}
                                        id="student-list"
                                        options={unexclude_candidates ? unexclude_candidates : []}
                                        getOptionLabel={(unexclude_candidates) => unexclude_candidates.first_name + " " + unexclude_candidates.last_name}
                                        getOptionKey={(student) => student.id}
                                        filterSelectedOptions
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="숙제를 부여하고 싶은 학생을 선택하세요."
                                            />
                                        )}
                                        sx={{ width: "100%" }}
                                        onChange={(event, value) => {
                                            setNewUnexcludeStudents(value);
                                        }}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                </Box>
                                </>
                            ) : (
                            <></>
                            )
                        }
                            <TextField
                                id="filled-basic"
                                label="Instructions"
                                variant="filled"
                                name='instructions'
                                fullWidth={true}
                                multiline={true}
                                rows={isHwQuestions ? 5 : 20}
                                sx={{
                                    minWidth: "100%",
                                    borderRadius: "5px",
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: colors.primary[600],
                                        ":focus": {
                                            backgroundColor: 'white',
                                        },
                                    },
                                }}
                                defaultValue={homework.content}
                            />

                            {/* ── Due Date Section (ALWAYS shown, for both types) ── */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: "100%", textAlign: "end" }}>
                                <Typography variant="h6" color="text.secondary">
                                    Please set the due date for homework to be one day before your class at 11:55 PM (ET). <br />
                                    (e.g Class Date: Friday, 10/11 Due Date: Thursday, 10/10, at 11:55 PM (ET))
                                </Typography>
                            </Box>
                            <Box display="flex" minWidth="100%" flexDirection="row" justifyContent="flex-end" gap={2}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DateTimePicker
                                        label="Due Date"
                                        value={dueDate}
                                        onChange={(newValue) => setDueDate(newValue)}
                                    />
                                </LocalizationProvider>
                                <TextField
                                    id="filled-basic"
                                    label="NY Time"
                                    variant="outlined"
                                    disabled
                                    value={dayjs(dueDate).tz('America/New_York').format('MM/DD/YYYY HH:mm')}
                                />
                            </Box>

                            {/* ── Conditional Section: HW Questions vs File Upload ── */}
                            {isHwQuestions ? (
                                /* ── HW Questions Edit Section ── */
                                <Box sx={{ minWidth: "100%", mt: 2 }}>
                                    {/* Status Banner */}
                                    {hwQuestionsEditMode === 'blocked' && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            Students have already submitted their answers. Page ranges and question selections cannot be modified.
                                        </Alert>
                                    )}
                                    {hwQuestionsEditMode === 'warning' && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            Some students have saved draft answers (not yet submitted). Changing pages or question selections may affect their saved work.
                                        </Alert>
                                    )}

                                    {/* Class Material (read-only) */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Class Material:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {matchedClassMaterial ? matchedClassMaterial.name : "Class material not found"}
                                        </Typography>
                                    </Box>

                                    {matchedClassMaterial ? (
                                        <>
                                            {/* Page Ranges Editor */}
                                            <Box display="flex" flexDirection="column" sx={{ gap: "0.5rem", mb: 2 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Page Ranges:
                                                </Typography>

                                                {pageRanges.map((range, index) => (
                                                    <Box key={index} display="flex" alignItems="center" sx={{ gap: "1rem" }}>
                                                        <FormControl sx={{ minWidth: 120, backgroundColor: colors.primary[600] }}
                                                                     disabled={hwQuestionsEditMode === 'blocked'}>
                                                            <InputLabel>From</InputLabel>
                                                            <Select
                                                                value={range.from}
                                                                label="From"
                                                                onChange={(e) => updatePageRange(index, 'from', e.target.value)}
                                                            >
                                                                <MenuItem value={0}>None</MenuItem>
                                                                {Array.from(
                                                                    { length: matchedClassMaterial?.textbook?.pages || 0 },
                                                                    (_, i) => i + 1
                                                                ).map((page) => (
                                                                    <MenuItem key={page} value={page}>{page}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>

                                                        <Typography>&rarr;</Typography>

                                                        <FormControl sx={{ minWidth: 120, backgroundColor: colors.primary[600] }}
                                                                     disabled={hwQuestionsEditMode === 'blocked' || range.from === 0}>
                                                            <InputLabel>To</InputLabel>
                                                            <Select
                                                                value={range.to}
                                                                label="To"
                                                                onChange={(e) => updatePageRange(index, 'to', e.target.value)}
                                                            >
                                                                <MenuItem value={0}>None</MenuItem>
                                                                {Array.from(
                                                                    { length: matchedClassMaterial?.textbook?.pages || 0 },
                                                                    (_, i) => i + 1
                                                                ).filter(page => page >= range.from)
                                                                 .map((page) => (
                                                                    <MenuItem key={page} value={page}>{page}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>

                                                        {pageRanges.length > 1 && hwQuestionsEditMode !== 'blocked' && (
                                                            <IconButton onClick={() => removePageRange(index)} size="small">
                                                                <CloseIcon />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}

                                                {hwQuestionsEditMode !== 'blocked' && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={addPageRange}
                                                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                                                    >
                                                        + Add Page Range
                                                    </Button>
                                                )}

                                                {(hasValidRanges() || autoRemovedPages.length > 0) && (
                                                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                                        Selected pages:{' '}
                                                        {[...getSelectedPages(), ...autoRemovedPages.filter(p => !getSelectedPages().includes(p))]
                                                            .sort((a, b) => a - b)
                                                            .map((page, idx, arr) => (
                                                                <span key={page}>
                                                                    <span style={autoRemovedPages.includes(page)
                                                                        ? { textDecoration: 'line-through', opacity: 0.5 }
                                                                        : {}
                                                                    }>
                                                                        {page}
                                                                    </span>
                                                                    {idx < arr.length - 1 && ', '}
                                                                </span>
                                                            ))
                                                        }
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Questions List */}
                                            <Box display="flex" flexDirection="column" sx={{ gap: "1rem" }}>
                                                <Typography variant="h6" color="text.secondary">
                                                    Total Questions: {hwQuestions.length}
                                                </Typography>

                                                {tinyLoading && (
                                                    <Box display="flex" justifyContent="center" p={2}>
                                                        <CircularProgress size={20} />
                                                    </Box>
                                                )}

                                                {hwQuestions.length > 0 && !tinyLoading && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                Loaded Questions ({hwQuestions.filter(q => !excludedQuestions.includes(q.id)).length} included / {hwQuestions.length} total)
                                                            </Typography>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => setShowQuestions(!showQuestions)}
                                                            >
                                                                {showQuestions ? 'Hide Questions' : 'Show Questions'}
                                                            </Button>
                                                        </Box>

                                                        {showQuestions && hwQuestions.map((question) => (
                                                            <Card
                                                                key={question.id}
                                                                sx={{
                                                                    backgroundColor: excludedQuestions.includes(question.id)
                                                                        ? colors.primary[700]
                                                                        : colors.primary[600],
                                                                    opacity: excludedQuestions.includes(question.id) ? 0.6 : 1,
                                                                }}
                                                            >
                                                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                                                                        <Box flex={1}>
                                                                            <Typography variant="subtitle1" fontWeight="bold">
                                                                                Page {question.page} | Q {question.question_label}
                                                                            </Typography>
                                                                            {question.content && (
                                                                                <Box sx={{ mt: 0.5 }}>
                                                                                    <QuestionRender content={question.content} />
                                                                                </Box>
                                                                            )}
                                                                            {/* Options for multiple choice */}
                                                                            {question.options && question.options.length > 0 && (
                                                                                <Box sx={{ mt: 1 }}>
                                                                                    <Typography variant="caption" fontWeight="bold">Options:</Typography>
                                                                                    <RadioGroup value="">
                                                                                        {question.options.map((option, idx) => {
                                                                                            const isCorrectAnswer = (answer, index) => {
                                                                                                if (typeof answer === 'number') return answer === index;
                                                                                                if (typeof answer === 'string') {
                                                                                                    const num = parseInt(answer, 10);
                                                                                                    return !isNaN(num) && String(num) === answer.trim() && num === index;
                                                                                                }
                                                                                                return false;
                                                                                            };
                                                                                            const isCorrect = Array.isArray(question.answer)
                                                                                                ? question.answer.some(ans => isCorrectAnswer(ans, idx))
                                                                                                : isCorrectAnswer(question.answer, idx);
                                                                                            return (
                                                                                                <Box key={idx} sx={{ ml: 1, display: 'flex', alignItems: 'flex-start' }}>
                                                                                                    <Radio checked={isCorrect} disabled size="small"
                                                                                                        sx={{ p: 0.5, color: isCorrect ? colors.greenAccent[400] : undefined,
                                                                                                            '&.Mui-checked': { color: colors.greenAccent[400] } }}
                                                                                                    />
                                                                                                    <Box sx={{ ml: 0.5 }}>
                                                                                                        <QuestionRender content={option} />
                                                                                                    </Box>
                                                                                                </Box>
                                                                                            );
                                                                                        })}
                                                                                    </RadioGroup>
                                                                                </Box>
                                                                            )}
                                                                            {/* Correct answer display */}
                                                                            {question.answer && question.answer.length > 0 && (
                                                                                <Box sx={{ mt: 1, p: 1, bgcolor: colors.greenAccent[800], borderRadius: '4px' }}>
                                                                                    <Typography variant="caption" fontWeight="bold" color={colors.greenAccent[300]}>
                                                                                        Correct Answer(s):
                                                                                    </Typography>
                                                                                    <Box sx={{ mt: 0.5 }}>
                                                                                        {question.answer_tool === 'coordinate' ? (
                                                                                            formatCoordinateAnswerDisplay(question.answer, question.answer_config, colors)
                                                                                        ) : question.answer_tool === 'table' ? (
                                                                                            (() => {
                                                                                                const config = question.answer_config || defaultTableConfig;
                                                                                                const answerFromConfig = {
                                                                                                    rows: (config.rows || []).map(row => ({
                                                                                                        id: row.id,
                                                                                                        cells: Object.fromEntries(
                                                                                                            Object.entries(row.cells || {}).map(([colId, cell]) => [colId, cell?.value || ''])
                                                                                                        )
                                                                                                    }))
                                                                                                };
                                                                                                return <TableReadOnly config={config} studentAnswer={answerFromConfig} showCorrect={false} />;
                                                                                            })()
                                                                                        ) : question.answer_tool === 'mapping' ? (
                                                                                            (() => {
                                                                                                const config = question.answer_config || defaultMappingConfig;
                                                                                                const answerString = Array.isArray(question.answer) ? question.answer[0] : (question.answer || '{"connections": []}');
                                                                                                return <MappingReadOnly config={config} studentAnswer={answerString} correctAnswer={answerString} showCorrectAnswer={false} showGradeBadge={false} />;
                                                                                            })()
                                                                                        ) : question.answer_tool === 'polynomialGraph' ? (
                                                                                            (() => {
                                                                                                let answerData = { correctGraphIndex: 0 };
                                                                                                try {
                                                                                                    if (question.answer && question.answer[0]) {
                                                                                                        answerData = typeof question.answer[0] === 'string'
                                                                                                            ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                                                    }
                                                                                                } catch (e) {
                                                                                                    console.error('Error parsing polynomial graph answer:', e);
                                                                                                }
                                                                                                return (
                                                                                                    <PolynomialGraphReadOnly
                                                                                                        config={question.answer_config || defaultPolynomialGraphConfig}
                                                                                                        studentAnswer={answerData}
                                                                                                        correctAnswer={answerData}
                                                                                                    />
                                                                                                );
                                                                                            })()
                                                                                        ) : question.answer_tool === 'rationalGraph' ? (
                                                                                            (() => {
                                                                                                let answerData = { correctGraphIndex: 0 };
                                                                                                try {
                                                                                                    if (question.answer && question.answer[0]) {
                                                                                                        answerData = typeof question.answer[0] === 'string'
                                                                                                            ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                                                    }
                                                                                                } catch (e) {
                                                                                                    console.error('Error parsing rational graph answer:', e);
                                                                                                }
                                                                                                return (
                                                                                                    <RationalGraphReadOnly
                                                                                                        config={question.answer_config || defaultRationalGraphConfig}
                                                                                                        studentAnswer={answerData}
                                                                                                        correctAnswer={answerData}
                                                                                                    />
                                                                                                );
                                                                                            })()
                                                                                        ) : question.answer_tool === 'trigGraph' ? (
                                                                                            (() => {
                                                                                                let answerData = { correctGraphIndex: 0 };
                                                                                                try {
                                                                                                    if (question.answer && question.answer[0]) {
                                                                                                        answerData = typeof question.answer[0] === 'string'
                                                                                                            ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                                                    }
                                                                                                } catch (e) {
                                                                                                    console.error('Error parsing trig graph answer:', e);
                                                                                                }
                                                                                                return (
                                                                                                    <TrigGraphReadOnly
                                                                                                        config={question.answer_config || defaultTrigGraphConfig}
                                                                                                        studentAnswer={answerData}
                                                                                                        correctAnswer={answerData}
                                                                                                    />
                                                                                                );
                                                                                            })()
                                                                                        ) : question.answer_tool === 'numberline' ? (
                                                                                            (() => {
                                                                                                let answerData = { elements: [] };
                                                                                                try {
                                                                                                    if (question.answer && question.answer[0]) {
                                                                                                        answerData = typeof question.answer[0] === 'string'
                                                                                                            ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                                                    }
                                                                                                } catch (e) {
                                                                                                    // Malformed numberline answer JSON — fall back to empty elements
                                                                                                }
                                                                                                return (
                                                                                                    <NumberLineReadOnly
                                                                                                        config={question.answer_config || defaultNumberLineConfig}
                                                                                                        studentAnswer={answerData}
                                                                                                        showCorrect={false}
                                                                                                    />
                                                                                                );
                                                                                            })()
                                                                                        ) : question.answer_tool === 'multiline' ? (
                                                                                            (() => {
                                                                                                let answerData = { rows: [] };
                                                                                                try {
                                                                                                    if (question.answer && question.answer[0]) {
                                                                                                        answerData = typeof question.answer[0] === 'string'
                                                                                                            ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                                                    }
                                                                                                } catch (e) {
                                                                                                    // Malformed answer JSON — fall back to empty rows
                                                                                                }
                                                                                                return (
                                                                                                    <MultilineReadOnly
                                                                                                        config={question.answer_config || defaultMultilineConfig}
                                                                                                        studentAnswer={answerData}
                                                                                                        showCorrect={false}
                                                                                                    />
                                                                                                );
                                                                                            })()
                                                                                        ) : question.answer_tool === 'mathlive' ? (
                                                                                            (() => {
                                                                                                const answers = Array.isArray(question.answer)
                                                                                                    ? question.answer
                                                                                                    : [question.answer];
                                                                                                return answers.map((rawAns, idx) => {
                                                                                                    const parsed = parseMathliveAnswer(rawAns);
                                                                                                    if (parsed?.type === MATHLIVE_ANSWER_TYPES.TEXT) {
                                                                                                        return (
                                                                                                            <Box key={idx} sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                                <Chip label="Text" size="small" />
                                                                                                                <Typography variant="body2">{parsed.value}</Typography>
                                                                                                            </Box>
                                                                                                        );
                                                                                                    }
                                                                                                    return (
                                                                                                        <Box key={idx} sx={{ ml: 1 }}>
                                                                                                            <QuestionRender content={String(parsed?.value ?? rawAns ?? '')} />
                                                                                                        </Box>
                                                                                                    );
                                                                                                });
                                                                                            })()
                                                                                        ) : Array.isArray(question.answer) ? (
                                                                                            question.answer.map((ans, idx) => {
                                                                                                const displayAnswer = question.question_type === 'multiple_choice' && question.options
                                                                                                    ? (question.options[parseInt(ans, 10)] || ans) : ans;
                                                                                                return <Box key={idx} sx={{ ml: 1 }}><QuestionRender content={String(displayAnswer)} /></Box>;
                                                                                            })
                                                                                        ) : (
                                                                                            <QuestionRender content={
                                                                                                question.question_type === 'multiple_choice' && question.options
                                                                                                    ? (question.options[parseInt(question.answer, 10)] || question.answer)
                                                                                                    : question.answer
                                                                                            } />
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={!excludedQuestions.includes(question.id)}
                                                                                    disabled={hwQuestionsEditMode === 'blocked'}
                                                                                    onChange={(e) => {
                                                                                        if (e.target.checked) {
                                                                                            setExcludedQuestions(excludedQuestions.filter(id => id !== question.id));
                                                                                            setExcludedQuestionLabels(excludedQuestionLabels.filter(l => l !== question.question_label));
                                                                                            // Restore page to pageRanges if it was auto-removed
                                                                                            const pageNum = question.page;
                                                                                            if (autoRemovedPages.includes(pageNum)) {
                                                                                                setPageRanges(pagesToRanges([...getSelectedPages(), pageNum]));
                                                                                                setAutoRemovedPages(prev => prev.filter(p => p !== pageNum));
                                                                                            }
                                                                                        } else {
                                                                                            const newExcluded = [...excludedQuestions, question.id];
                                                                                            setExcludedQuestions(newExcluded);
                                                                                            setExcludedQuestionLabels([...excludedQuestionLabels, question.question_label]);

                                                                                            // Auto-remove page from pageRanges if all questions on this page are now excluded
                                                                                            const pageNum = question.page;
                                                                                            const questionsOnPage = hwQuestions.filter(q => q.page === pageNum);
                                                                                            const allExcluded = questionsOnPage.every(q => newExcluded.includes(q.id));
                                                                                            if (allExcluded) {
                                                                                                const updatedRanges = [];
                                                                                                pageRanges.forEach(range => {
                                                                                                    if (range.from > 0 && range.to >= range.from && pageNum >= range.from && pageNum <= range.to) {
                                                                                                        // This range contains the page to remove
                                                                                                        if (range.from === range.to) {
                                                                                                            // Single-page range, remove entirely
                                                                                                        } else if (pageNum === range.from) {
                                                                                                            updatedRanges.push({ from: range.from + 1, to: range.to });
                                                                                                        } else if (pageNum === range.to) {
                                                                                                            updatedRanges.push({ from: range.from, to: range.to - 1 });
                                                                                                        } else {
                                                                                                            // Split into two ranges
                                                                                                            updatedRanges.push({ from: range.from, to: pageNum - 1 });
                                                                                                            updatedRanges.push({ from: pageNum + 1, to: range.to });
                                                                                                        }
                                                                                                    } else {
                                                                                                        updatedRanges.push(range);
                                                                                                    }
                                                                                                });
                                                                                                setAutoRemovedPages(prev => [...prev, pageNum]);
                                                                                                if (updatedRanges.length === 0) {
                                                                                                    setPageRanges([{ from: 0, to: 0 }]);
                                                                                                } else {
                                                                                                    setPageRanges(updatedRanges);
                                                                                                }
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            }
                                                                            label="Include"
                                                                        />
                                                                    </Box>
                                                                </CardContent>
                                                            </Card>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </>
                                    ) : (
                                        <Alert severity="info">
                                            The class material associated with this homework is no longer available. Page and question editing is disabled.
                                        </Alert>
                                    )}
                                </Box>
                            ) : (
                                /* ── Existing File Upload UI ── */
                                <Box display="flex" minWidth="100%" flexDirection="column" justifyContent="flex-start" gap={2}>
                                    <Box display="flex" alignItems="center" mb={-2}>
                                        <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                                            Upload Files
                                        </Typography>
                                        <IconButton component="label">
                                            <FileUploadOutlined />
                                            <input multiple={true} styles={{display:"none"}} type="file" hidden onChange={handleUpload} name="[licenseFile]" />
                                        </IconButton>
                                    </Box>
                                    <Box display="flex" minWidth="100%" flexDirection="column">
                                        {files.length > 0 && files.map((file, index) => (
                                            <Box key={index} display="flex" alignItems="center">
                                                <Typography variant="body1" color={colors.primary[450]}>{file.name}</Typography>
                                                <IconButton onClick={(e) => handleFileRemove(e, index)}><ClearIcon /></IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                style={{
                                    backgroundColor: colors.primary[300],
                                    color: "white",
                                    float: "right",
                                    marginTop: "0.5rem",
                                    marginBottom: "1rem",
                                    width: "10rem",
                                }}
                            >
                                Update
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
}
